import {
  getBackendAssetUrl,
  getBackendCurrencyValue,
  getDateFromIsoString,
} from '@shared/api';
import {createTranslationDict} from '@shared/helpers';
import {getLocalizedText} from '@shared/utils';
import {Category, SubCategory} from '../../category';
import {
  Event,
  EventDisplayType,
  EventParticipant,
  EventResult,
  EventStatus,
  GroupEvent,
  ParticipantLogoOption,
} from '../types';
import {
  EventListItemOnBackend,
  EventOnBackend,
  GroupEventOnBackend,
} from './backendTypes';

const createFallbackCategory = (name?: string | null): Category => ({
  id: '',
  name: name ?? 'Unknown',
  description: '',
  logoUrl: '',
  bannerUrl: '',
  order: 0,
});

const getCategory = (
  categories: Category[],
  categoryName?: unknown,
): Category => {
  const localizedCategoryName = getLocalizedText(categoryName);
  const foundCategory = localizedCategoryName
    ? categories.find(cat => cat.name === localizedCategoryName)
    : undefined;

  return (
    foundCategory ??
    categories[0] ??
    createFallbackCategory(localizedCategoryName)
  );
};

const mapSubCategoryFromBackend = (subCategory: unknown): SubCategory => {
  const subCategoryObject =
    subCategory &&
    typeof subCategory === 'object' &&
    !Array.isArray(subCategory)
      ? (subCategory as Record<string, unknown>)
      : {};

  const id =
    typeof subCategoryObject.id === 'string' && subCategoryObject.id
      ? subCategoryObject.id
      : typeof subCategoryObject.slug === 'string'
        ? subCategoryObject.slug
        : '';

  return {
    id,
    name: getLocalizedText(subCategoryObject.name),
    slug:
      typeof subCategoryObject.slug === 'string' && subCategoryObject.slug
        ? subCategoryObject.slug
        : id,
  };
};

const mapGroupEventFromBackend = (g: GroupEventOnBackend): GroupEvent => ({
  id: g.id,
  eventId: g.event_id ?? g.slug ?? g.id,
  name: getLocalizedText(g.name),
  shortName: getLocalizedText(g.short_name) || getLocalizedText(g.name),
  description: getLocalizedText(g.description),
  logoUrl: getBackendAssetUrl(g.logo_url),
  shortLogoUrl: getBackendAssetUrl(g.short_logo_url),
  groupOrder: g.group_order ?? 0,
  groupType: g.group_type ?? 0,
  tradingVolume: g.trading_volume ?? 0,
  yesPrice: getBackendCurrencyValue(g.yes_price) ?? 0,
  noPrice: getBackendCurrencyValue(g.no_price) ?? 0,
});

const mapEventParticipant = (
  p:
    | {
        id: string;
        name?: string | null;
        logo_url?: string | null;
        logo_url_stroke?: string | null;
        logo_url_player?: string | null;
        logo_url_custom?: string | null;
        nation?: string | null;
      }
    | null
    | undefined,
): EventParticipant | null =>
  p
    ? {
        id: p.id,
        name: p.name ?? '',
        logoUrl: p.logo_url ?? null,
        logoUrlStroke: p.logo_url_stroke ?? null,
        logoUrlPlayer: p.logo_url_player ?? null,
        logoUrlCustom: p.logo_url_custom ?? null,
        nation: p.nation ?? null,
      }
    : null;

export const mapEventFromBackend = (
  e: EventOnBackend | EventListItemOnBackend,
  categories: Category[],
): Event => {
  const subCategoriesRaw = e.sub_categories ?? e.subcategories ?? [];
  const groupEventsRaw = e.group ?? e.groups ?? [];
  // Participant fields exist only on the detailed GET /events/{id} response.
  const detailed = e as Partial<EventOnBackend>;

  return {
    id: e.id,
    eventId: e.event_id ?? e.slug ?? e.id,
    name: getLocalizedText(e.name),
    nameTranslations: createTranslationDict(e.name),
    shortName: getLocalizedText(e.short_name) || getLocalizedText(e.name),
    shortNameTranslations: createTranslationDict(e.short_name),
    holders: e.holders ?? 0,
    participants: e.participants ?? 0,
    description: getLocalizedText(e.description),
    descriptionTranslations: createTranslationDict(e.description),
    logoUrl: getBackendAssetUrl(e.logo_url),
    shortLogoUrl: getBackendAssetUrl(e.short_logo_url),
    category: getCategory(categories, e.category_name),
    subCategories: subCategoriesRaw.map(subCategory =>
      mapSubCategoryFromBackend(subCategory),
    ),
    geoSlugs: (e.geo_slugs ?? []).filter(
      (g): g is string => typeof g === 'string',
    ),
    startDate: getDateFromIsoString(e.start_date),
    endDate: getDateFromIsoString(e.end_date ?? e.start_date),
    liveDate: e.live_date ? getDateFromIsoString(e.live_date) : null,
    isLive: e.is_live ?? false,
    autoPublishEnabled: e.auto_publish_enabled ?? false,
    isFavorite: e.is_favorite ?? false,
    isPromoted: e.is_promoted ?? false,
    yesPrice: getBackendCurrencyValue(e.yes_price) ?? 0,
    noPrice: getBackendCurrencyValue(e.no_price) ?? 0,
    yesText: getLocalizedText(e.yes_text),
    yesTextTranslations: createTranslationDict(e.yes_text),
    noText: getLocalizedText(e.no_text),
    noTextTranslations: createTranslationDict(e.no_text),
    overview: getLocalizedText(e.tabs?.overview),
    overviewTranslations: createTranslationDict(e.tabs?.overview),
    sourceOfTruth: getLocalizedText(e.tabs?.source_of_truth),
    sourceOfTruthTranslations: createTranslationDict(e.tabs?.source_of_truth),
    rules: getLocalizedText(e.tabs?.rules),
    rulesTranslations: createTranslationDict(e.tabs?.rules),
    tradingVolume: e.trading_volume ?? 0,
    status: e.status ?? EventStatus.null,
    result: e.result ?? EventResult.null,
    topic: e.topic
      ? {
          id: e.topic.id,
          topicId:
            e.topic.topic_id ??
            e.topic.topic_slug ??
            e.topic.slug ??
            e.topic.id,
          name: getLocalizedText(e.topic.name),
          logoUrl: getBackendAssetUrl(e.topic.logo_url) || null,
        }
      : null,
    group: groupEventsRaw.map(groupEvent =>
      mapGroupEventFromBackend(groupEvent),
    ),
    groupId: e.group_id ?? null,
    displayType:
      (e.display_type as EventDisplayType) ?? EventDisplayType.fixedLive,
    participantLogoOption:
      (e.participant_logo_option as ParticipantLogoOption | undefined) ?? null,
    participantShortLogoOption:
      (e.participant_short_logo_option as ParticipantLogoOption | undefined) ??
      null,
    participantId: detailed.participant_id ?? null,
    externalParticipantId: detailed.external_participant_id ?? null,
    yesParticipantId: detailed.yes_participant_id ?? null,
    externalYesParticipantId: detailed.external_yes_participant_id ?? null,
    noParticipantId: detailed.no_participant_id ?? null,
    externalNoParticipantId: detailed.external_no_participant_id ?? null,
    participant: mapEventParticipant(detailed.participant),
    yesParticipant: mapEventParticipant(detailed.yes_participant),
    noParticipant: mapEventParticipant(detailed.no_participant),
  };
};
